import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DeadlineDateCalcualator } from '@zaoblako/shared/common/classes/deadline.date.calcualator';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { ClientFindInterface } from '@zaoblako/shared/modules/client/dtos/client/client-find.dto';
import { EntityValidatorClass } from '@zaoblako/shared/modules/eav/classes/entity-validator.class';
import type { EavAttributeFindInterface } from '@zaoblako/shared/modules/eav/dtos/attribute/eav-attribute-find.dto';
import { HelpdeskNotificationSettingsFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-notification-settings.dto';
import type { HelpdeskTaskAuditorUpdateInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-auditor-update.dto';
import type { HelpdeskTaskStatusFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-status-find.dto';
import type { HelpdeskTaskFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-find.dto';
import { HelpdeskTaskStatusEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.status.enum';
import {
  HelpdeskTaskFormDto,
  type HelpdeskTaskFormInterface
} from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-form.dto';
import type { HelpdeskTaskTypeFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-find.dto';
import type { HelpdeskTaskTypeListQueryInterface } from '@zaoblako/shared/modules/helpdesk/dto/task-type/helpdesk-task-type-list-query.dto';
import type { HelpdeskTaskTemplateFindInterface } from '@zaoblako/shared/modules/helpdesk/dto/template/helpdesk-task-template-find.dto';
import { HelpdeskTaskPriorityEnum } from '@zaoblako/shared/modules/helpdesk/enum/helpdesk.task.priority.enum';
import type { ProjectFindInterface } from '@zaoblako/shared/modules/project/dto/project/project-find.dto';
import type { HelpdeskTaskRequiredFieldsInterface } from '@zaoblako/shared/modules/helpdesk/dto/helpdesk-task-required-fields.dto';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { AccessService } from '@zaoblako/web/core/models/enums/access-rules/access.service';
import { ClientService } from '@zaoblako/web/core/services/client/client.service';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { DealService } from '@zaoblako/web/core/services/deal/deal.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { GlobalService } from '@zaoblako/web/core/services/global.service';
import { HelpDeskSettingsService } from '@zaoblako/web/core/services/helpdesk/help-desk-settings.service';
import { HelpDeskTaskTemplateService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-template.service';
import { HelpDeskTaskTypeService } from '@zaoblako/web/core/services/helpdesk/help-desk-task-type.service';
import { HelpDeskTaskService } from '@zaoblako/web/core/services/helpdesk/help-desk-task.service';
import { HdPrefixService } from '@zaoblako/web/core/services/prefix/hd-prefix.service';
import { ProjectService } from '@zaoblako/web/core/services/project/project.service';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { TemplateEntityVariableService } from '@zaoblako/web/core/services/template-entity-variable.service';
import { FilesListComponent } from '@zaoblako/web/shared/components/files-list/files-list.component';
import { TypeFormEnum } from '@zaoblako/web/shared/components/form-sidebar/enums/type.form.enum';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { VoiceAssistantBridgeService } from '@zaoblako/web/shared/voice-assistant/services/voice-assistant-bridge.service';
import { VoiceAssistantFormEnum } from '@zaoblako/shared/modules/ai/enums/voice-assistant-form.enum';
import { ClientAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/client-auto-complete/client-auto-complete.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { ProjectAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/project-auto-complete/project-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { DaterangepickerComponent } from '@zaoblako/web/shared/dumbs/daterangepicker/daterangepicker.component';
import { ExternalUserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-multi-select/external-user-multi-select.component';
import { ExternalUserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/client/external-user-single-select/external-user-single-select.component';
import { CostCenterMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-multi-select/cost-center-multi-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { FilialMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-multi-select/filial-multi-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { HdTaskTypeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/hd-task-type-single-select/hd-task-type-single-select.component';
import { HdEavFieldsComponent } from '@zaoblako/web/shared/dumbs/hd-eav-fields/hd-eav-fields.component';
import { MasterMilestoneSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/master-milestone-single-select/master-milestone-single-select.component';
import { UserGroupMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-group-multi-select/user-group-multi-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { RoomMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-multi-select/room-multi-select.component';
import { RoomSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/room-single-select/room-single-select.component';
import { HelpdeskTaskPriorityNamePipe } from '@zaoblako/web/shared/pipes/helpdesk-task-priority-name.pipe';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { OrderByPipe } from '@zaoblako/web/shared/pipes/order-by.pipe';
import moment from 'moment-timezone';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DialogService } from 'primeng/dynamicdialog';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { uniqBy } from 'lodash';
import '@zaoblako/shared/modules/security/dtos/room-access/room-access-find.dto';
import '@zaoblako/shared/modules/messenger/dtos/thread/thread-find.dto';

@Component({
  standalone: true,
  selector: 'crm-helpdesk-task-form',
  templateUrl: './helpdesk-task-form.component.html',
  imports: [
    CommonModule,
    CardModule,
    JoinPipe,
    ExternalUserSingleSelectComponent,
    ButtonComponent,
    SingleSelectComponent,
    HdTaskTypeSingleSelectComponent,
    InputNumberModule,
    FormsModule,
    EditorComponent,
    UserGroupMultiSelectComponent,
    ProjectAutoCompleteComponent,
    DatepickerComponent,
    HelpdeskTaskPriorityNamePipe,
    SpinnerComponent,
    DealAutoCompleteComponent,
    DividerModule,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    MasterMilestoneSingleSelectComponent,
    ExternalUserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    CheckboxModule,
    InputTextComponent,
    DaterangepickerComponent,
    FileUploadModule,
    FilesListComponent,
    FilialMultiSelectComponent,
    CostCenterMultiSelectComponent,
    RoomMultiSelectComponent,
    RoomSingleSelectComponent,
    OrderByPipe,
    ClientAutoCompleteComponent,
    HdEavFieldsComponent
  ],
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HelpdeskTaskFormComponent
  extends BaseFormComponent<HelpdeskTaskFormInterface>
  implements OnInit, OnDestroy {
  @Input() public readonly index: number;
  public task = <HelpdeskTaskFormInterface>{};
  public dto = HelpdeskTaskFormDto;
  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public taskTypes: HelpdeskTaskTypeFindInterface[] = [];
  public taskTypesLoading = false;
  public clientLoading = false;
  public requiredFields: HelpdeskTaskRequiredFieldsInterface;
  public settings: HelpdeskNotificationSettingsFindInterface;

  public externalUsers: ExternalUserFindInterface[] = [];
  public externalUsersLoading = false;
  public auditors: HelpdeskTaskAuditorUpdateInterface[] = [];
  public priorities = Object.keys(HelpdeskTaskPriorityEnum);

  /** Status assigned to new tasks (used for EAV required-on-status rules on create). */
  private newTaskStatus?: HelpdeskTaskStatusFindInterface;

  public get isEditForm(): boolean {
    return !!this.formStoreService.getState.forms[this.index]?.isEdit;
  }

  /** Current or initial HD status for EAV required-on-status rules. */
  public get hdTaskStatusForEav(): HelpdeskTaskStatusFindInterface | undefined {
    return (this.task as HelpdeskTaskFindInterface)?.status ?? this.newTaskStatus;
  }

  public get hdTaskStatusId(): number | undefined {
    return this.hdTaskStatusForEav?.id;
  }

  private formSubscription$: Subscription = new Subscription();

  private voiceAssistantClientSubscription$: Subscription;

  private voiceAssistantHdTaskTypeSubscription$: Subscription;
  private taskTypesLoadCount = 0;
  private clientLoadCount = 0;
  private externalUsersRequestId = 0;

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly helpdeskTaskService: HelpDeskTaskService,
    private readonly clientService: ClientService,
    private readonly dealService: DealService,
    private readonly projectService: ProjectService,
    private readonly helpDeskTaskTypeService: HelpDeskTaskTypeService,
    private readonly helpDeskTaskTemplateService: HelpDeskTaskTemplateService,
    private readonly templateEntityVariableService: TemplateEntityVariableService,
    private readonly externalUserService: ExternalUserService,
    private readonly errorService: ErrorService,
    private readonly confirmService: ConfirmDialogService,
    private readonly router: Router,
    private readonly cdRef: ChangeDetectorRef,
    public readonly globalService: GlobalService,
    public readonly helpDeskSettingsService: HelpDeskSettingsService,
    public readonly hdPrefixService: HdPrefixService,
    private readonly fileService: FileService,
    private readonly voiceAssistantBridgeService: VoiceAssistantBridgeService,
    public readonly accessService: AccessService
  ) {
    super();
  }

  public ngOnInit(): void {
    const projectId = this.formStoreService.getState.forms[this.index]?.data?.projectId;
    const dealId = this.formStoreService.getState.forms[this.index]?.data?.dealId;
    const templateId = this.formStoreService.getState.forms[this.index]?.data?.templateId;
    const clientId = this.formStoreService.getState.forms[this.index]?.data?.clientId;
    const waitForInitialClient = !this.isEditForm;

    if (waitForInitialClient) {
      this.beginClientLoading();
    }

    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    this.voiceAssistantClientSubscription$ = this.voiceAssistantBridgeService
      .onFormSave(VoiceAssistantFormEnum.CLIENT)
      .subscribe((client) => this.confirmService.confirm({
        message: `Назначить заявке клиента "${client?.name}"?`,
        accept: () => {
          this.task.client = client;
          this.cdRef.markForCheck();
        }
      }));
    this.voiceAssistantHdTaskTypeSubscription$ = this.voiceAssistantBridgeService
      .onFormSave(VoiceAssistantFormEnum.HD_TASK_TYPE)
      .subscribe((taskType) => this.confirmService.confirm({
        message: `Назначить заявке тип "${taskType?.name}"?`,
        accept: () => {
          if (this.task.client) {
            this.clientService
              .findById<ClientFindInterface>(this.task.client.id)
              .subscribe(({ helpdeskTaskTypes, ...rest }) => {
                const client = {
                  ...rest,
                  helpdeskTaskTypes: [...(helpdeskTaskTypes || []), taskType]
                };
                this.clientService
                  .update(client)
                  .subscribe(() => {
                    this.task.client = client;
                    this.task.taskType = taskType;
                    this.cdRef.markForCheck();

                    this.loadTaskTypes();
                  });
              });
          } else {
            this.taskTypes = [...this.taskTypes, taskType];
            this.task.taskType = taskType;
            this.cdRef.markForCheck();
          }
        }
      }));

    if (!isNaN(projectId)) {
      this.withClientLoading(this.projectService.findById<ProjectFindInterface>(projectId)).subscribe((project) => {
        this.task.project = project;
        this.task.client = project.client;
        this.task.deadline = project.deadline;
        this.task.unit = project.unit;
        this.task.masterMilestone = project.masterMilestone;
        this.cdRef.markForCheck();
        this.loadTaskTypes();
      });
    }

    if (!isNaN(dealId) && !this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.withClientLoading(
        forkJoin([
          this.dealService.findById<DealFindInterface>(dealId),
          this.helpDeskSettingsService.getDefaultClient()
        ])
      ).subscribe(([deal, defaultClient]) => {
        this.task.deal = deal;
        if (deal.unit) {
          this.task.unit = deal.unit;
        }
        const clientFromContragent = deal.contragent?.client;
        if (clientFromContragent?.id) {
          this.task.client = clientFromContragent;
        } else {
          this.task.client = defaultClient;
        }
        this.cdRef.markForCheck();
        this.loadTaskTypes();
        if (this.task.client?.id) {
          this.loadExternalUsers(this.task.client.id);
        }
      });
    } else if (!isNaN(dealId)) {
      this.dealService.findById<DealFindInterface>(dealId).subscribe((deal) => {
        this.task.deal = deal;
        this.cdRef.markForCheck();
      });
    }

    if (!isNaN(templateId)) {
      this.withClientLoading(
        this.helpDeskTaskTemplateService.findById<HelpdeskTaskTemplateFindInterface>(templateId)
      ).subscribe((template) => {
        this.task = {
          client: template.client,
          externalUser: template.externalUser,
          project: template.project,
          deal: template.deal,
          responsible: template.responsible,
          taskType: template.taskType,
          unit: template.unit,
          description: this.templateEntityVariableService.parse(template.description),
          userAuditors: template.userAuditors,
          userGroupAuditors: template.userGroupAuditors,
          plannedMin: template.plannedMin,
          masterMilestone: template.masterMilestone,
          deadline: DeadlineDateCalcualator.getInstance().calculateDate(
            template.deadline,
            template.planning.nearestDate || template.planning.runDate
          )
        };
        this.cdRef.markForCheck();
        this.loadTaskTypes();
      });
    }

    forkJoin([
      this.helpdeskTaskService.getRequiredFields(),
      this.helpDeskSettingsService.getNotificationSettings(),
      this.helpdeskTaskService.getStatuses()
    ])
      .pipe(finalize(() => waitForInitialClient && this.endClientLoading()))
      .subscribe(([requiredFields, settings, statuses]) => {
        this.newTaskStatus = statuses.find(({ code }) => code === HelpdeskTaskStatusEnum.NEW);
        if (!isNaN(clientId) && !this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.withClientLoading(
            this.clientService.findById<ClientFindInterface>(clientId, {
              select: ['id', 'name', 'contragents']
            })
          ).subscribe({
            next: (client) => {
              if (client?.id) {
                this.task.client = client;
                this.cdRef.markForCheck();

                this.loadTaskTypes();

                this.loadExternalUsers(this.task.client.id);
              }
            }
          });
        }

        if (this.task.client?.id) {
          this.loadTaskTypes();
          this.loadExternalUsers(this.task.client.id);
        }
        this.requiredFields = requiredFields;
        this.settings = settings;
        this.cdRef.markForCheck();

        if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.withClientLoading(this.helpDeskSettingsService.getDefaultClient()).subscribe((defaultClient) => {
            if (!this.task.client) {
              this.task.client = defaultClient;
              this.cdRef.markForCheck();

              this.loadTaskTypes();

              this.loadExternalUsers(this.task.client?.id);
            }
          });
        }

        if (this.formStoreService.getState.forms[this.index]?.isEdit) {
          this.getById();
        }
      });
  }

  public ngOnDestroy(): void {
    this.formSubscription$?.unsubscribe();
    this.voiceAssistantClientSubscription$?.unsubscribe();
    this.voiceAssistantHdTaskTypeSubscription$?.unsubscribe();
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.helpdeskTaskService
      .findById<HelpdeskTaskFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (task) => {
          const clientId = task.client?.id;
          this.task = clientId ? { ...task, client: undefined } : task;

          this.task.userAuditors?.forEach((user) => {
            this.auditors.push({ user: user });
          });
          this.task.userGroupAuditors?.forEach((userGroup) => {
            this.auditors.push({ group: userGroup });
          });

          if (clientId) {
            this.withClientLoading(
              this.clientService.findById<ClientFindInterface>(clientId, {
                select: ['id', 'name', 'contragents']
              })
            ).subscribe({
              next: (client) => {
                this.task.client = client;
                this.loadExternalUsers(clientId);
                this.cdRef.markForCheck();
              },
              error: () => {
                this.loadExternalUsers(clientId);
                this.cdRef.markForCheck();
              }
            });
          }

          this.loadTaskTypes();
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        },
        error: () => {
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveTask() {
    if (this.task?.taskType?.entityType?.attributes?.length && this.task?.eavEntity?.values) {
      if (Object.keys(this.task?.eavEntity?.values).every((key) => !this.task?.eavEntity?.values[key])) {
        this.task.eavEntity.values = {};
      }
      const validator = EntityValidatorClass.getInstance().createSafeSyncValidator(
        this.task.taskType.entityType.attributes,
        {
          hdTaskStatus: this.hdTaskStatusForEav,
          transformValidationErrors: (values) => ({ eavEntity: { values } }),
          transformValidationMetadata: (nested) => ({
            eavEntity: {
              label: 'Дополнительные реквизиты',
              nested: {
                values: {
                  label: 'Значения',
                  nested
                }
              }
            }
          })
        }
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, errors] = validator(this.task.eavEntity.values);
      if (errors?.validationErrors) {
        this.errors = errors.validationErrors as any;
        return;
      }
    }
    if (this.checkValidate(this.task).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.helpdeskTaskService.update<HelpdeskTaskFormInterface, HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Заявка ${this.hdPrefixService.getPrefixedNumber(task)} обновлена`);
            this.formStoreService.saveNext(task, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.helpdeskTaskService.create<HelpdeskTaskFindInterface>(this.task).subscribe({
          next: (task) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Заявка создана');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router
                .navigate(['/helpdesk/task/card/', this.hdPrefixService.getPrefixedNumber(task)])
                .catch(console.error);
            } else {
              this.formStoreService.saveNext(task, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteTask(task: HelpdeskTaskFormInterface) {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить заявку ${this.hdPrefixService.getPrefixedNumber(
        task as HelpdeskTaskFindInterface
      )}?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.helpdeskTaskService
          .deleteById(this.formStoreService.getState.forms[this.index]?.entityId)
          .subscribe(() => {
            this.errorService.addSuccessMessage(
              `Заявка ${this.hdPrefixService.getPrefixedNumber(task as HelpdeskTaskFindInterface)} удалена`
            );
            this.formStoreService.deleteNext(this.index);
          });
      }
    });
  }

  public selectedClient(client: ClientFindInterface) {
    this.task.externalUser = null;
    this.task.taskType = null;
    this.cdRef.markForCheck();

    this.loadTaskTypes();

    if (client) {
      this.loadExternalUsers(client.id);
    } else {
      this.loadExternalUsers(undefined);
    }
    this.cdRef.markForCheck();
  }

  public openFormCreateExternalUser() {
    const result = this.formStoreService.addForm(TypeFormEnum.EXTERNAL_USER_CREATE, {
      headerText: 'Добавление внешнего пользователя',
      widthSize: 'form-sidebar-small',
      data: {
        client: this.task?.client
      }
    });

    this.formSubscription$.add(
      result.onSave.subscribe((externalUser: ExternalUserFindInterface) => {
        if (externalUser) {
          this.externalUsers.push(externalUser);
          this.externalUsers = [...this.externalUsers];
          this.task.externalUser = externalUser;
          this.cdRef.markForCheck();
        }
      })
    );
  }

  public selectTaskType() {
    this.setDefaultPriorityAndDeadline(this.task.taskType);
    this.setDefaultResponsibleAndUserGroups(this.task.taskType);
    this.setEntityType(this.task.taskType);
  }

  public setDefaultPriorityAndDeadline(taskType: HelpdeskTaskTypeFindInterface) {
    if (this.settings?.useTaskTypeDefaults && !this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.task.priority = null;
      this.task.deadline = null;
      if (taskType) {
        if (taskType.defaultPriority) {
          this.task.priority = taskType.defaultPriority;
        }
        if (taskType.defaultDeadline) {
          this.task.deadline = moment().add(taskType.defaultDeadline, 'day').toDate();
        }
      }
    }
  }

  public setDefaultResponsibleAndUserGroups(taskType: HelpdeskTaskTypeFindInterface) {
    if (!this.formStoreService.getState.forms[this.index]?.isEdit) {
      if (taskType?.responsible && !this.task.responsible) {
        this.task.responsible = taskType.responsible;
      }
      this.task.userGroupAuditors = uniqBy(
        [
          ...(this.task.userGroupAuditors || []),
          ...(taskType?.userGroupAuditors || [])
        ],
        'id'
      );
    }
  }

  public setEntityType(taskType: HelpdeskTaskTypeFindInterface) {
    if (!taskType.entityType?.attributes?.length) {
      this.task.eavEntity = null;
      return;
    }

    const values: Record<number, any> = taskType.entityType.attributes.reduce(
      (acc: Record<number, any>, attribute: EavAttributeFindInterface) => {
        acc[attribute.id] = this.task.eavEntity?.values ? this.task.eavEntity.values?.[attribute.id] : null;
        return acc;
      },
      {}
    );
    this.task.eavEntity = { values };
    this.cdRef.markForCheck();
  }

  private loadTaskTypes() {
    let queryParams: HelpdeskTaskTypeListQueryInterface = {};
    if (this.task?.client) {
      queryParams.clientIds = [this.task.client.id];
    }
    this.taskTypesLoadCount++;
    this.taskTypesLoading = true;
    this.cdRef.markForCheck();

    this.helpDeskTaskTypeService
      .find<HelpdeskTaskTypeFindInterface>(queryParams)
      .pipe(
        finalize(() => {
          this.taskTypesLoadCount--;
          this.taskTypesLoading = this.taskTypesLoadCount > 0;
          this.cdRef.markForCheck();
        })
      )
      .subscribe({
        next: (taskTypes) => {
          this.taskTypes = taskTypes;
          if (this.task.taskType) {
            this.task.taskType = taskTypes.find((type) => type?.id === this.task.taskType?.id);
          }
          this.cdRef.markForCheck();
        }
      });
  }

  private loadExternalUsers(clientId?: number): void {
    const requestId = ++this.externalUsersRequestId;
    if (!clientId) {
      this.externalUsers = [];
      this.externalUsersLoading = false;
      this.cdRef.markForCheck();
      return;
    }

    this.externalUsersLoading = true;
    this.cdRef.markForCheck();

    this.externalUserService
      .findManyFlat({ clientIds: [clientId] })
      .pipe(
        finalize(() => {
          if (requestId === this.externalUsersRequestId) {
            this.externalUsersLoading = false;
            this.cdRef.markForCheck();
          }
        })
      )
      .subscribe((externalUsers) => {
        if (requestId === this.externalUsersRequestId) {
          this.externalUsers = externalUsers ?? [];
          this.cdRef.markForCheck();
        }
      });
  }

  private withClientLoading<T>(source: Observable<T>): Observable<T> {
    this.beginClientLoading();

    return source.pipe(finalize(() => this.endClientLoading()));
  }

  private beginClientLoading(): void {
    this.clientLoadCount++;
    this.clientLoading = true;
    this.cdRef.markForCheck();
  }

  private endClientLoading(): void {
    this.clientLoadCount = Math.max(0, this.clientLoadCount - 1);
    this.clientLoading = this.clientLoadCount > 0;
    this.cdRef.markForCheck();
  }
}
